import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';

import { useTableContext } from 'lib/context/TableContext/TableContext';
import { mapValues } from 'lib/helpers/utility';
import { useAiPromptTemplateDescription } from 'lib/hooks/queries/admin/intelligence/useAiPromptTemplateDescription';

import {
  DEFAULT_COLUMN_DEFS,
  GRID_OPTIONS
} from 'components/admin/pages/Intelligence/Prompt/lib/helpers';
import AGTable from 'components/shared/AGTable/AGTable';
import { CustomStatusBarCount } from 'components/shared/AGTable/CustomStatusBarCount';
import { CustomStatusBarPagination } from 'components/shared/AGTable/CustomStatusBarPagination';
import DisplayButton from 'components/shared/AGTable/DisplayButton';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Filter from 'components/shared/Filter/Filter';
import Header from 'components/shared/Header/Header';

import { DEFAULT_FILTERS } from './lib/defaultFilters';

const AdminIntelligencePromptDescriptionTable = () => {
  const { toggleSwitch, setCount } = useOutletContext();

  const { page, setPage, sort, filters, setFilters, limit } = useTableContext();
  const [validTotal, setValidTotal] = useState(null);

  const { isLoading, isFetching, data } = useAiPromptTemplateDescription({
    params: {
      page,
      sort,
      limit,
      filters: mapValues(filters)
    },
    options: {},
    dependencies: [limit, page, sort, mapValues(filters)],
    name: 'ai_prompt_template_descriptions'
  });
  const [_, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (data?.results.total !== undefined) {
      setValidTotal(data.results.total);
    }
  }, [data?.results.total]);

  useEffect(() => {
    if (!isLoading || !isFetching) {
      setPage(1);
    }
  }, [validTotal]);

  useEffect(() => {
    setCount(data?.results.total);
  }, [data?.results.total, setCount]);

  const onPageChange = useCallback((currentPage) => setPage(currentPage), [setPage]);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: CustomStatusBarCount,
          statusPanelParams: {
            data: data?.results?.results,
            count: data?.results.total,
            page,
            limit,
            pagination: true
          },
          align: 'left'
        },
        {
          statusPanel: CustomStatusBarPagination,
          statusPanelParams: {
            data: data?.results?.results,
            count: data?.results.total,
            page,
            limit,
            onPageChange
          },
          align: 'right'
        }
      ]
    };
  }, [data?.results?.results, data?.results.total, page, limit, onPageChange]);

  return (
    <>
      <Header title="MyScribe AI" childrenClassName="w-full">
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          {toggleSwitch}
          <div className="flex flex-1 items-center justify-end gap-2">
            <DisplayButton />
            <Filter
              category="admin_intelligence_ai_prompt_template_table"
              defaultFilters={DEFAULT_FILTERS}
              filters={filters}
              setFilters={setFilters}
              menuPortalTarget={document.body}
            />
          </div>
        </div>
      </Header>
      <div className="ag-theme-quartz !mb-0  h-full">
        <AGTable
          loading={isLoading || isFetching}
          defaultColDef={DEFAULT_COLUMN_DEFS}
          gridOptions={GRID_OPTIONS}
          defaultFilters={DEFAULT_FILTERS}
          animateRows={true}
          data={data?.results?.results}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          isRowSelectable={() => true}
          customClassName="ag-grid-interactive"
          enableRangeSelection={true}
          statusBar={statusBar}
          reactiveCustomComponents
          onRowClicked={(e) => {
            setSearchParams({ promptId: e?.data?.id });
          }}
        />
      </div>
    </>
  );
};

export default withErrorBoundary(AdminIntelligencePromptDescriptionTable);
